import { BELT_KEYS, KEYS } from "../../game/player-input.ts";

/** Nome curto de uma tecla (código do teclado) para os textos do tutorial. */
export function keyLabel(code: string): string {
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  const names: Record<string, string> = {
    Tab: "Tab",
    Enter: "Enter",
    Space: "Espaço",
    ShiftLeft: "Shift",
    ShiftRight: "Shift",
    ControlLeft: "Ctrl",
    MouseMiddle: "botão do meio",
    MouseRight: "botão direito",
    ArrowUp: "↑",
    ArrowDown: "↓",
    ArrowLeft: "←",
    ArrowRight: "→",
    ControlRight: "Ctrl dir.",
    AltLeft: "Alt",
    AltRight: "Alt Gr",
    CapsLock: "Caps Lock",
    Backspace: "Backspace",
    Backquote: "`",
    Minus: "-",
    Equal: "=",
    BracketLeft: "[",
    BracketRight: "]",
    Backslash: "\\",
    Semicolon: ";",
    Quote: "'",
    Comma: ",",
    Period: ".",
    Slash: "/",
    IntlRo: "/ (ABNT)",
    IntlBackslash: "\\ (ABNT)",
    NumpadEnter: "Enter (num.)",
  };
  if (code.startsWith("Numpad")) return `Num ${code.slice(6)}`;
  return names[code] ?? code;
}

/** Teclas configuradas que os textos citam (doc 06: "as teclas configuradas aparecem no texto"). */
export interface TutorialKeys {
  belt: string;
  inventory: string;
  crafting: string;
  rotate: string;
}

export function tutorialKeys(keys: { [K in keyof typeof KEYS]: readonly string[] } = KEYS, belt: readonly string[] = BELT_KEYS): TutorialKeys {
  const two = (codes: readonly string[]) => [...new Set(codes.slice(0, 2).map(keyLabel))].join(" ou ");
  return {
    belt: `${keyLabel(belt[0] ?? "Digit1")} a ${keyLabel(belt[belt.length - 1] ?? "Digit8")}`,
    inventory: two(keys.inventory),
    crafting: two(keys.crafting),
    rotate: two(keys.rotate),
  };
}

/** Para onde a seta aponta em cada passo (o cliente acha o alvo mais perto). */
export type TutorialTarget = "galhada" | "arvore" | "bancada";

export interface TutorialStep {
  id: "coletar" | "cinto" | "inventario" | "ferramenta" | "arvore" | "bancada" | "construcao" | "comer";
  title: string;
  text: (k: TutorialKeys) => string;
  target?: TutorialTarget;
}

/**
 * Passos (roadmap F28: coleta, cinto, inventário, árvores, Bancada, construção; doc 06: fabricar
 * uma ferramenta e comer). Curtos, por jogador e sem bloquear nada.
 */
export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  { id: "coletar", title: "Coletar", text: () => "Mire numa galhada ou moita no chão e clique (segure para repetir).", target: "galhada" },
  { id: "cinto", title: "Cinto", text: (k) => `Escolha um espaço do cinto com ${k.belt} ou a roda do mouse.` },
  { id: "inventario", title: "Inventário", text: (k) => `Abra o inventário com ${k.inventory}.` },
  { id: "ferramenta", title: "Ferramenta", text: (k) => `Fabrique um Machado de Madeira (${k.crafting}). A corda sai da palha das moitas.` },
  { id: "arvore", title: "Árvores", text: () => "Com o machado na mão, derrube uma árvore.", target: "arvore" },
  { id: "bancada", title: "Bancada", text: (k) => `Fabrique a Bancada (${k.crafting}) e, com ela na mão, posicione com um clique.` },
  { id: "construcao", title: "Construção", text: (k) => `Perto da Bancada, fabrique um Piso de Tábuas e, com ele na mão, construa com um clique (${k.rotate} gira).`, target: "bancada" },
  { id: "comer", title: "Comer", text: () => "Com comida na mão, segure o clique para comer." },
];

export type TutorialStepId = TutorialStep["id"];

/** O que a cena observa na partida (contadores desde o começo dela). */
export interface TutorialSignals {
  /** Galhadas e moitas que este jogador colheu (golpe aceito pelo servidor que derrubou o recurso). */
  collected: number;
  beltPicks: number;
  inventoryOpened: boolean;
  /** Já teve um machado nesta partida (fica verdadeiro mesmo se guardar ou entregar). */
  hasAxe: boolean;
  /** Árvores que este jogador derrubou (o golpe aceito que fez a árvore cair). */
  treesFelled: number;
  bancadaPlaced: boolean;
  piecesBuilt: number;
  ate: boolean;
}

export const NO_SIGNALS: TutorialSignals = { collected: 0, beltPicks: 0, inventoryOpened: false, hasAxe: false, treesFelled: 0, bancadaPlaced: false, piecesBuilt: 0, ate: false };

/** O passo está feito? (quem já fez antes de chegar nele passa direto). */
export function stepDone(id: TutorialStepId, s: TutorialSignals): boolean {
  switch (id) {
    case "coletar":
      return s.collected > 0;
    case "cinto":
      return s.beltPicks > 0;
    case "inventario":
      return s.inventoryOpened;
    case "ferramenta":
      return s.hasAxe;
    case "arvore":
      return s.treesFelled > 0;
    case "bancada":
      return s.bancadaPlaced;
    case "construcao":
      return s.piecesBuilt > 0;
    case "comer":
      return s.ate;
  }
}

/** Guardado no navegador, versionado (doc 06: `localStorage` versionado). */
export const TUTORIAL_PREF = "tutorial";
export interface TutorialSave {
  v: 1;
  enabled: boolean;
  done: TutorialStepId[];
  /** Sobe a cada mudança de preferência (desligar, religar): distingue reinício de progresso entre abas. */
  epoch: number;
}

const IDS = new Set<string>(TUTORIAL_STEPS.map((s) => s.id));

/** Lê o que estava guardado; ausente, de outra versão ou inválido → ligado e do começo. */
export function parseTutorial(raw: string | null | undefined): TutorialSave {
  try {
    const data = JSON.parse(raw ?? "") as Partial<TutorialSave>;
    if (data.v !== 1 || typeof data.enabled !== "boolean" || !Array.isArray(data.done)) throw new Error("versão");
    const epoch = typeof data.epoch === "number" && Number.isFinite(data.epoch) ? data.epoch : 0;
    return { v: 1, enabled: data.enabled, done: data.done.filter((id): id is TutorialStepId => typeof id === "string" && IDS.has(id)), epoch };
  } catch {
    return { v: 1, enabled: true, done: [], epoch: 0 };
  }
}

/**
 * Junta o que esta aba sabe com o que outra gravou: preferência mais nova (época maior) vence
 * inteira; na mesma época, os passos feitos se somam (nenhuma aba apaga o progresso da outra).
 */
export function mergeTutorial(mine: TutorialSave, stored: TutorialSave): TutorialSave {
  if (stored.epoch > mine.epoch) return { ...stored, done: [...stored.done] };
  if (mine.epoch > stored.epoch) return { ...mine, done: [...mine.done] };
  const done = TUTORIAL_STEPS.map((s) => s.id).filter((id) => mine.done.includes(id) || stored.done.includes(id));
  return { v: 1, enabled: mine.enabled && stored.enabled, done, epoch: mine.epoch };
}

/** Quanto tempo a tarefa concluída fica riscada antes da próxima (doc 06: some em 1,5 s). */
export const DONE_MS = 1500;

export interface TutorialView {
  title: string;
  text: string;
  index: number;
  total: number;
  done: boolean;
  target?: TutorialTarget;
}

/**
 * Cartão de objetivos (F81, mockup ui-b: três linhas com o círculo de check): o passo anterior (feito), a
 * tarefa à vista e o próximo passo, pelos títulos. `index` é o da vista (1 a `total`).
 */
export function objectiveNeighbors(index: number): { previous?: string; next?: string } {
  const out: { previous?: string; next?: string } = {};
  const previous = TUTORIAL_STEPS[index - 2];
  const next = TUTORIAL_STEPS[index];
  if (index > 1 && previous) out.previous = previous.title;
  if (next) out.next = next.title;
  return out;
}

/** Progresso do tutorial deste jogador: o primeiro passo não feito, com a tarefa riscada por 1,5 s. */
export class Tutorial {
  private save: TutorialSave;
  private finished: { step: TutorialStep; until: number } | undefined;

  constructor(save: TutorialSave) {
    this.save = { ...save, done: [...save.done] };
  }

  get enabled(): boolean {
    return this.save.enabled;
  }

  get done(): readonly TutorialStepId[] {
    return this.save.done;
  }

  get current(): TutorialStep | undefined {
    if (!this.save.enabled) return undefined;
    return TUTORIAL_STEPS.find((s) => !this.save.done.includes(s.id));
  }

  /** Confere o passo atual; devolve o id concluído agora (para salvar). */
  update(signals: TutorialSignals, now: number): TutorialStepId | undefined {
    if (this.finished && now < this.finished.until) return undefined;
    this.finished = undefined;
    const step = this.current;
    if (!step || !stepDone(step.id, signals)) return undefined;
    this.save.done.push(step.id);
    this.finished = { step, until: now + DONE_MS };
    return step.id;
  }

  /** Pula o passo à vista (não o seguinte: durante o risco de 1,5 s, não faz nada). */
  skip(now: number): void {
    if (this.finished && now < this.finished.until) return;
    const step = this.current;
    if (!step) return;
    this.save.done.push(step.id);
    this.finished = { step, until: now + DONE_MS };
  }

  disable(): void {
    this.save = { ...this.save, enabled: false, epoch: this.save.epoch + 1 };
    this.finished = undefined;
  }

  /** Passa a valer o guardado junto (outra aba, ou a junção antes de gravar). */
  adopt(save: TutorialSave): void {
    this.save = { ...save, done: [...save.done] };
    if (!save.enabled) this.finished = undefined;
  }

  view(now: number, keys: TutorialKeys): TutorialView | undefined {
    const shown = this.finished && now < this.finished.until ? this.finished.step : this.current;
    if (!shown || !this.save.enabled) return undefined;
    const index = TUTORIAL_STEPS.indexOf(shown);
    const view: TutorialView = { title: shown.title, text: shown.text(keys), index: index + 1, total: TUTORIAL_STEPS.length, done: shown !== this.current };
    if (shown.target && shown === this.current) view.target = shown.target;
    return view;
  }

  toJSON(): TutorialSave {
    return { v: 1, enabled: this.save.enabled, done: [...this.save.done], epoch: this.save.epoch };
  }
}

/** Opção do menu: ligar volta do começo; desligar guarda o que já foi feito. Os dois sobem a época. */
export function setTutorialEnabled(save: TutorialSave, enabled: boolean): TutorialSave {
  if (enabled === save.enabled) return save;
  return enabled ? { v: 1, enabled: true, done: [], epoch: save.epoch + 1 } : { ...save, enabled: false, epoch: save.epoch + 1 };
}
